import { useCallback } from "react";

import { useNavigate, useLocation } from "react-router-dom";

const REDIRECT_PATH_KEY = "redirectPath";
const REDIRECT_PATH_MAX_AGE_MS = 30 * 60 * 1000;

type StoredRedirectPath = {
  path: string;
  storedAt: number;
};

const isSafeRedirectPath = (path: string | null): path is string => {
  return Boolean(path && path.startsWith("/") && !path.startsWith("//"));
};

const readRedirectPath = (rawValue: string | null): string | null => {
  if (!rawValue) return null;

  try {
    const parsed = JSON.parse(rawValue) as Partial<StoredRedirectPath>;
    if (
      typeof parsed.path === "string" &&
      typeof parsed.storedAt === "number" &&
      Date.now() - parsed.storedAt <= REDIRECT_PATH_MAX_AGE_MS &&
      isSafeRedirectPath(parsed.path)
    ) {
      return parsed.path;
    }
    return null;
  } catch {
    return isSafeRedirectPath(rawValue) ? rawValue : null;
  }
};

export const storeRedirectPath = (path: string) => {
  if (!isSafeRedirectPath(path)) return;

  const value = JSON.stringify({ path, storedAt: Date.now() });
  try {
    sessionStorage.setItem(REDIRECT_PATH_KEY, value);
  } catch {
    // Best effort: storage can be unavailable in some browser privacy modes.
  }
  try {
    localStorage.setItem(REDIRECT_PATH_KEY, value);
  } catch {
    // Local storage lets magic-link callbacks restore redirects across tabs.
  }
};

const clearStoredRedirectPath = () => {
  try {
    sessionStorage.removeItem(REDIRECT_PATH_KEY);
  } catch {
    // Best effort.
  }
  try {
    localStorage.removeItem(REDIRECT_PATH_KEY);
  } catch {
    // Best effort.
  }
};

const getStorageItem = (storage: Storage) => {
  try {
    return storage.getItem(REDIRECT_PATH_KEY);
  } catch {
    return null;
  }
};

const getStoredPath = () => {
  const path =
    readRedirectPath(getStorageItem(sessionStorage)) ??
    readRedirectPath(getStorageItem(localStorage));
  clearStoredRedirectPath();
  return path;
};

export function useAuthRedirect() {
  const location = useLocation();
  const navigate = useNavigate();

  return {
    handleUnauthenticated: useCallback(() => {
      const fullPath = `${location.pathname}${location.search}${location.hash}`;
      storeRedirectPath(fullPath);
      navigate("/login");
    }, [location.hash, location.pathname, location.search, navigate]),

    redirectAfterLogin: useCallback(() => {
      const storedPath = getStoredPath();
      if (storedPath) {
        navigate(storedPath);
      } else {
        navigate("/");
      }
    }, [navigate]),
  };
}
